import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  ButtonColor,
  ButtonComponent,
  ButtonSize,
} from '@freelancer/ui/button';
import { CalloutComponent, CalloutPlacement } from '@freelancer/ui/callout';
import { IconColor, IconModule, IconSize } from '@freelancer/ui/icon';
import { PictureComponent, PictureDisplay } from '@freelancer/ui/picture';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import { CalloutContentComponent } from '../callout/callout-content.component';
import { CalloutTriggerComponent } from '../callout/callout-trigger.component';
import { BottomDrawerSize, DrawerComponent, DrawerPosition } from '../drawer';
import { LinkColor, LinkHoverColor, LinkUnderline } from '../link';
import {
  DropdownFilterContentSize,
  DropdownFilterIconColor,
  type DropdownFilterIconName,
  DropdownFilterSize,
  ICONS_NO_FILL,
} from './dropdown-filter.types';
import { LinkModule } from '../link/link.module';
import { UnreadIndicatorComponent } from '../unread-indicator/unread-indicator.component';

@Component({
  standalone: true,
  selector: 'fl-dropdown-filter',
  imports: [
    ButtonComponent,
    CalloutComponent,
    CalloutContentComponent,
    CalloutTriggerComponent,
    DrawerComponent,
    IconModule,
    LinkModule,
    NgTemplateOutlet,
    PictureComponent,
    UnreadIndicatorComponent,
  ],
  template: `
    @if (useBottomDrawer) {
      <div class="mobile:show">
        <div
          class="DropdownFilterTrigger"
          (click)="openMobileDrawer()"
        >
          @if (!iconOnly && !isTextButton) {
            <fl-button
              class="DropdownFilterButton"
              [class.IsActive]="isActive"
              [class.BorderRadius]="borderRadius"
              [color]="ButtonColor.CUSTOM"
              [display]="'block'"
              [size]="size"
              [disabled]="disabled"
              [attr.data-applied]="filterApplied"
            >
              <ng-container [ngTemplateOutlet]="dropdownContent"></ng-container>
            </fl-button>
          }
          @if (!iconOnly && isTextButton) {
            <fl-link
              [class.IsActive]="isActive"
              [color]="LinkColor.INHERIT"
              [disabled]="disabled"
              [hoverColor]="LinkHoverColor.INHERIT"
              [underline]="LinkUnderline.NEVER"
              [size]="linkSize"
              [attr.data-applied]="filterApplied"
            >
              <ng-container [ngTemplateOutlet]="dropdownContent"></ng-container>
            </fl-link>
          }
          <ng-container *ngTemplateOutlet="iconTrigger"></ng-container>
        </div>

        @if (isClientSide) {
          <fl-drawer
            #drawerBottom
            [position]="DrawerPosition.BOTTOM"
            [edgeToEdge]="true"
            [bottomDrawerSize]="BottomDrawerSize.FIT_CONTENT"
          >
            <ng-template #drawerTemplate>
              <div class="DropdownFilterContentWrapper">
                <div
                  class="DropdownFilterContent text-foreground font-normal text-body-16"
                  [attr.data-edge-to-edge]="edgeToEdge"
                  (click)="handleContentClicked()"
                >
                  <ng-container
                    *ngTemplateOutlet="projectedContent"
                  ></ng-container>
                </div>
              </div>
            </ng-template>
          </fl-drawer>
        }
      </div>
    }

    <div [class.mobile:hide]="useBottomDrawer">
      <fl-callout
        [edgeToEdge]="true"
        [hideCloseButton]="true"
        [placement]="contentPlacement"
        [mobileCloseButton]="false"
        [mobileDropdown]="mobileDropdown"
        [useSlideAnimation]="!useBottomDrawer"
        (calloutOpen)="toggleState()"
        (calloutClose)="handleCalloutClose()"
      >
        <fl-callout-trigger
          class="DropdownFilterTrigger"
          [attr.disabled]="disabled"
          (trigger)="handleTrigger()"
        >
          @if (!iconOnly && !isTextButton) {
            <fl-button
              class="DropdownFilterButton"
              #button
              [class.IsActive]="isActive"
              [class.BorderRadius]="borderRadius"
              [color]="ButtonColor.CUSTOM"
              [display]="'block'"
              [size]="size"
              [disabled]="disabled"
              [attr.data-applied]="filterApplied"
            >
              <ng-container [ngTemplateOutlet]="dropdownContent"></ng-container>
            </fl-button>
          }
          @if (!iconOnly && isTextButton) {
            <fl-link
              #button
              [class.IsActive]="isActive"
              [color]="LinkColor.INHERIT"
              [disabled]="disabled"
              [hoverColor]="LinkHoverColor.INHERIT"
              [underline]="LinkUnderline.NEVER"
              [size]="linkSize"
              [attr.data-applied]="filterApplied"
            >
              <ng-container [ngTemplateOutlet]="dropdownContent"></ng-container>
            </fl-link>
          }
          <ng-container *ngTemplateOutlet="iconTrigger"></ng-container>
        </fl-callout-trigger>

        <fl-callout-content>
          <div class="DropdownFilterContentWrapper">
            <div
              class="DropdownFilterContent text-foreground font-normal text-body-16"
              #content
              [attr.data-edge-to-edge]="edgeToEdge"
              (click)="handleContentClicked()"
            >
              <ng-container *ngTemplateOutlet="projectedContent"></ng-container>
            </div>
          </div>
        </fl-callout-content>
      </fl-callout>
    </div>

    <ng-template #dropdownContent>
      <div [class.DropdownFilterLabel]="!hideDropdownIcon">
        <div class="DropdownFilterButtonInner">
          @if (iconName) {
            <fl-icon
              class="DropdownFilterIcon mr-xxsmall"
              [label]="iconLabel"
              [name]="iconName"
              [size]="IconSize.SMALL"
              [showTitle]="iconShowTitle"
              [useIconFont]="true"
              [fillIconFont]="iconFill ?? !ICONS_NO_FILL.includes(iconName)"
            ></fl-icon>
          }
          @if (logoSrc && !iconName) {
            <div class="DropdownLogo mr-xxsmall">
              <fl-picture
                [src]="logoSrc"
                [alt]="logoAlt"
                [fullWidth]="true"
                [display]="PictureDisplay.BLOCK"
              ></fl-picture>
            </div>
          }
          <div
            class="DropdownFilterButtonText"
            [class.mr-xxsmall]="!hideDropdownIcon"
          >
            @if (filterApplied && buttonTextApplied) {
              {{ buttonTextApplied }}
            } @else {
              {{ buttonText }}
            }
          </div>
        </div>
        @if (!hideDropdownIcon) {
          <fl-icon
            [name]="'ui-chevron-down'"
            [size]="IconSize.MID"
            [useIconFont]="true"
            [fillIconFont]="true"
            [weight]="FontWeight.MEDIUM"
          ></fl-icon>
        }
      </div>
    </ng-template>

    <ng-template #iconTrigger>
      @if (iconOnly) {
        @if (iconName) {
          <fl-icon
            [clickable]="true"
            [color]="iconColor"
            [label]="iconLabel"
            [name]="iconName"
            [size]="iconSize"
            [showTitle]="iconShowTitle"
            [class.IconPaddingMessaging]="isMessaging"
            [useIconFont]="true"
            [fillIconFont]="iconFill ?? !ICONS_NO_FILL.includes(iconName)"
          ></fl-icon>
        }
        @if (unreadIndicator) {
          <div
            class="UnreadPosition"
            [class.UnreadPositionMessaging]="isMessaging"
          >
            <fl-unread-indicator
              label="Unread Indicator"
              i18n-label="Unread indicator label"
            ></fl-unread-indicator>
          </div>
        }
      }
    </ng-template>

    <ng-template #projectedContent>
      <ng-content></ng-content>
    </ng-template>
  `,
  styleUrls: ['./dropdown-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownFilterComponent {
  private renderer = inject(Renderer2);
  private platformId = inject(PLATFORM_ID);

  BottomDrawerSize = BottomDrawerSize;
  ButtonColor = ButtonColor;
  DrawerPosition = DrawerPosition;
  IconSize = IconSize;
  FontWeight = FontWeight;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  LinkUnderline = LinkUnderline;
  PictureDisplay = PictureDisplay;

  ICONS_NO_FILL = ICONS_NO_FILL;

  isActive = false;
  isClientSide = isPlatformBrowser(this.platformId);

  @Input() buttonText: string;
  /** Text to display when the filter is applied. Uses the default text if this is undefined */
  @Input() buttonTextApplied?: string;
  @Input() contentPlacement = CalloutPlacement.BOTTOM;
  @Input() contentSize = DropdownFilterContentSize.FLUID;
  @Input() size: DropdownFilterSize = ButtonSize.SMALL;
  @Input() edgeToEdge = false;
  @Input() hideDropdownIcon = false;
  @Input() iconName?: DropdownFilterIconName;
  /**
   * Overrides the default fill behaviour for `iconName`. By default an icon is
   * filled, set to `false` to force it to render as an outline.
   */
  @Input() iconFill?: boolean;
  @Input() iconLabel?: string;
  @Input() iconShowTitle = true;
  @Input() logoSrc?: string;
  @Input() logoAlt = '';
  @Input() unreadIndicator = false;
  @Input() linkSize: TextSize = TextSize.BODY_16;
  @Input() isMessaging = false;

  @HostBinding('attr.disabled')
  @Input()
  disabled = false;

  @HostBinding('attr.data-display')
  @Input()
  display: 'block' | 'inline' = 'inline';

  @Input() filterApplied = false;

  @Input() borderRadius = false;

  /** Automatically close when a value is selected */
  @Input() autoClose = false;

  @Input() iconOnly = false;
  @Input() isTextButton = false;

  /**
   * When `true`, opens a bottom drawer on mobile instead of the inline
   * callout. Defaults to `false` so existing usages keep the original callout
   * behavior and aren't affected by the drawer's back-button history
   * management (which can revert URL-driven filter state on auto-close).
   */
  @Input() useBottomDrawer = false;

  /**
   * When `true`, the dropdown opens tethered to the trigger on mobile (flipping
   * above or below based on available space) with a dimmed backdrop, instead of
   * a centered dialog. Has no effect when `useBottomDrawer` is `true`. Defaults
   * to `false` to preserve the existing centered behavior.
   */
  @Input() mobileDropdown = false;

  /** This only works with iconOnly, and only supports IconColor.FOREGROUND and IconColor.LIGHT. It changes the color of the icon */
  @Input() iconColor: DropdownFilterIconColor = IconColor.FOREGROUND;

  /** This only works with iconOnly. It changes the size of the icon */
  @Input() iconSize = IconSize.MID;

  @Output() calloutClose = new EventEmitter<void>();

  @ViewChild('button', { read: ElementRef })
  button: ElementRef<HTMLDivElement>;

  @ViewChild('content', { read: ElementRef })
  content: ElementRef<HTMLDivElement>;

  @ViewChild(CalloutComponent)
  calloutComponent: CalloutComponent;

  @ViewChild('drawerBottom') drawerBottom: DrawerComponent;

  // FIXME: T300116 Enabling hydration errors on the webapp server
  // TypeError: Cannot read properties of null (reading 'parentElement')
  @HostBinding('attr.ngSkipHydration') skipHydration = 'true';

  close(): void {
    this.calloutComponent?.close();
    this.drawerBottom?.close();
  }

  openMobileDrawer(): void {
    if (!this.disabled) {
      this.drawerBottom?.open();
    }
  }

  protected handleTrigger(): void {
    // Update callout width when we're about to display it
    // so we'll always get the updated trigger width
    if (this.contentSize === DropdownFilterContentSize.INHERIT) {
      this.setContentWidth();
    }
  }

  protected toggleState(): void {
    this.isActive = !this.isActive;
  }

  protected handleCalloutClose(): void {
    this.toggleState();
    this.calloutClose.emit();
  }

  protected handleContentClicked(): void {
    if (this.autoClose) {
      this.close();
    }
  }

  /**
   * Sets callout content's width with the trigger/button's width
   */
  private setContentWidth(): void {
    const buttonWidth = this.button.nativeElement.getBoundingClientRect().width;

    this.renderer.setStyle(
      this.content.nativeElement,
      'width',
      `${buttonWidth}px`,
    );
  }
}
